import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

export interface SearchStoreState {
  // Search input values (temp values while user is typing)
  searchInputValues: Record<string, unknown>;
  // Committed search params (used for actual search)
  committedSearchParams: Record<string, unknown>;
  // Pagination state
  currentPage: number;
  itemsPerPage: number;
  // Sorting state
  sortField: string | null;
  sortOrder: 'asc' | 'desc';
}

export interface SearchStoreActions {
  setSearchInputValues: (values: Record<string, unknown>) => void;
  setCommittedSearchParams: (params: Record<string, unknown>) => void;
  setCurrentPage: (page: number) => void;
  setItemsPerPage: (itemsPerPage: number) => void;
  setSortField: (field: string | null) => void;
  setSortOrder: (order: 'asc' | 'desc') => void;
  clearSearch: () => void;
  resetAll: () => void;
}

export interface SearchStore extends SearchStoreState, SearchStoreActions {}

export interface CreateSearchStoreOptions {
  storeName: string;
  defaultSortField?: string | null;
  defaultSortOrder?: 'asc' | 'desc';
  defaultItemsPerPage?: number;
  defaultSearchValues?: Record<string, unknown>;
}

export const createSearchStore = ({
  storeName,
  defaultSortField = 'updatedAt',
  defaultSortOrder = 'desc',
  defaultItemsPerPage = 100,
  defaultSearchValues = {},
}: CreateSearchStoreOptions) => {
  const initialState: SearchStoreState = {
    searchInputValues: { ...defaultSearchValues },
    committedSearchParams: { ...defaultSearchValues },
    currentPage: 1,
    itemsPerPage: defaultItemsPerPage,
    sortField: defaultSortField,
    sortOrder: defaultSortOrder,
  };

  return create<SearchStore>()(
    devtools(
      (set) => ({
        ...initialState,

        setSearchInputValues: (values) => {
          set({ searchInputValues: values }, false, 'setSearchInputValues');
        },

        setCommittedSearchParams: (params) => {
          set({ committedSearchParams: params }, false, 'setCommittedSearchParams');
        },

        setCurrentPage: (page) => {
          set({ currentPage: page }, false, 'setCurrentPage');
        },

        setItemsPerPage: (itemsPerPage) => set({ itemsPerPage }, false, 'setItemsPerPage'),

        setSortField: (field) => set({ sortField: field }, false, 'setSortField'),

        setSortOrder: (order) => set({ sortOrder: order }, false, 'setSortOrder'),

        clearSearch: () =>
          set(
            {
              searchInputValues: { ...defaultSearchValues },
              committedSearchParams: { ...defaultSearchValues },
              currentPage: 1,
            },
            false,
            'clearSearch'
          ),

        resetAll: () => set(initialState, false, 'resetAll'),
      }),
      {
        name: storeName.toLowerCase().replace(/store$/i, '') + '-store',
      }
    )
  );
};
